import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { Product } from "@aurelia/types";
import { PriceTag, getDiscountPercent } from "@aurelia/ui";

interface FloatingCardProps {
  product: Product;
  size: "lg" | "sm";
  className?: string;
  rotate?: number;
  delay: number;
  floatDelay: number;
  priority?: boolean;
}

function FloatingCard({ product, size, className, rotate = 0, delay, floatDelay, priority }: FloatingCardProps) {
  // This showcase only ever renders at lg+ (see `hidden lg:flex` below), so
  // these are sized for the 1024–1279px range first, then grow at xl —
  // narrower than that, the lg-to-xl container itself is too narrow for the
  // larger size without the secondary cards' badges clipping under the
  // center card (verified via elementFromPoint at 1024/1280/1440/1536).
  const dimension = size === "lg" ? "w-56 h-56 xl:w-80 xl:h-80" : "w-32 h-32 xl:w-48 xl:h-48";
  // Same source of truth as the Best Sellers product cards — never a
  // hardcoded number, and never shown unless there's a genuine discount.
  const discountPercent = getDiscountPercent(product.basePrice, product.compareAtPrice);

  return (
    <motion.div
      initial={{ opacity: 0, y: 32, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute animate-float ${className ?? ""}`}
      style={{ animationDelay: `${floatDelay}s` }}
    >
      <Link
        to={`/product/${product.slug}`}
        className={`group block rounded-3xl border border-white/15 bg-white/[0.05] backdrop-blur-2xl p-2.5 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.6)] transition-transform duration-300 hover:-translate-y-1.5 hover:border-[#dcb662]/40 ${dimension}`}
        style={{ transform: `rotate(${rotate}deg)` }}
      >
        <div className="h-full flex flex-col">
          <div className="relative flex-1 rounded-2xl overflow-hidden bg-gradient-to-br from-white/10 to-white/0">
            {product.images[0] && (
              <img
                src={product.images[0]}
                alt={product.name}
                loading={priority ? "eager" : "lazy"}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            )}
            {discountPercent !== null && (
              <span className="absolute top-3 left-3 rounded-full bg-red-500 text-white text-xs font-medium px-2.5 py-1 z-10">
                {discountPercent}% OFF
              </span>
            )}
          </div>
          <div className={size === "lg" ? "pt-2" : "pt-1.5"}>
            <p
              className={
                size === "lg"
                  ? "text-base font-medium text-[#f6f1e7]/90 truncate"
                  : "text-sm font-medium text-[#f6f1e7]/90 truncate"
              }
            >
              {product.name}
            </p>
            <PriceTag
              cents={product.basePrice}
              compareAtCents={product.compareAtPrice ?? undefined}
              className={size === "lg" ? "text-sm text-[#dcb662]" : "text-xs text-[#dcb662]"}
            />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

// Real product photos, layered for depth beside the headline on desktop —
// one large featured card plus up to two smaller floating cards. Degrades
// gracefully as the catalog grows: with only one product today it shows
// just the large card rather than repeating the same photo to fake variety.
export function HeroShowcase({ products }: { products: Product[] }) {
  const [featured, ...rest] = products;
  const secondary = rest.slice(0, 2);
  if (!featured) return null;

  return (
    <div className="relative hidden lg:flex items-center justify-center h-[500px]">
      <div
        aria-hidden
        className="absolute h-[380px] w-[380px] rounded-full opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(220,182,98,0.3), rgba(92,124,250,0.1) 55%, transparent 75%)"
        }}
      />
      <svg aria-hidden className="absolute h-[420px] w-[420px] opacity-30" viewBox="0 0 420 420">
        <ellipse cx={210} cy={210} rx={200} ry={138} fill="none" stroke="#dcb662" strokeWidth={1} />
      </svg>

      {secondary[0] && (
        <FloatingCard
          product={secondary[0]}
          size="sm"
          className="-top-4 -left-9 z-10"
          rotate={-8}
          delay={0.7}
          floatDelay={0}
        />
      )}

      <FloatingCard
        product={featured}
        size="lg"
        className="z-20"
        rotate={2}
        delay={0.55}
        floatDelay={1.6}
        priority
      />

      {secondary[1] && (
        <FloatingCard
          product={secondary[1]}
          size="sm"
          className="-bottom-4 -right-9 z-10"
          rotate={7}
          delay={0.85}
          floatDelay={3.2}
        />
      )}
    </div>
  );
}
